import { apiClient } from './api';
import type { PendingProduct, ProductSearchResult } from '../types/products';
import type { PaginationInfo } from '../types/supplies';

export const productsService = {
  // Update is_viewed for a pending product
  // Update is_viewed for a pending product (notes, photos, followup)
  updatePendingIsViewed: async (
    pendingProductId: number,
    notesViewed: boolean,
    photosViewed: boolean,
    followUpViewed: boolean
  ): Promise<PendingProduct> => {
    const response = await apiClient.patch<{ data: PendingProduct }>(
      `/products/pending/${pendingProductId}/viewed`,
      { notesViewed, photosViewed, followUpViewed }
    );
    return response.data!.data;
  },
  // Update notes for a pending product
  updatePendingProductNotes: async (pendingProductId: number, notes: string): Promise<PendingProduct> => {
    const response = await apiClient.patch<{ data: PendingProduct }>(`/products/pending/${pendingProductId}/notes`, { notes });
    return response.data!.data;
  },
  // Assign supplier to pending product
  assignPendingProductSupplier: async (pendingProductId: number, supplier: string): Promise<PendingProduct> => {
    const response = await apiClient.patch<{ data: PendingProduct }>(`/products/pending/${pendingProductId}/assign-supplier`, { supplier });
    return response.data!.data;
  },
  // Update status for a pending product
  updatePendingProductStatus: async (pendingProductId: number, status: 'pending' | 'blocked' | 'unavailable'): Promise<PendingProduct> => {
    const response = await apiClient.patch<{ data: PendingProduct }>(`/products/pending/${pendingProductId}/status`, { status });
    return response.data!.data;
  },
  // Product Search
  searchProducts: async (search: string, limit = 20): Promise<ProductSearchResult[]> => {
    const response = await apiClient.get<ProductSearchResult[]>(`/products/search?search=${encodeURIComponent(search)}&limit=${limit}`);
    return response.data!;
  },

  getProductByCode: async (itemCode: number): Promise<ProductSearchResult> => {
    const response = await apiClient.get<ProductSearchResult>(`/products/item/${itemCode}`);
    return response.data!;
  },

  getProductClasses: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/products/classes');
    return response.data!;
  },
  
  getProductSuppliers: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`/products/suppliers`);
    return response.data || [];
  },

  // Pending Products
  getPendingProducts: async (filters: {
    branch_id?: number;
    date_from?: string;
    date_to?: string;
    item_code?: string;
    item_name?: string;
    status?: 'pending' | 'blocked' | 'unavailable';
    prod_class?: string;
    stock_status?: 'in stock' | 'out of stock';
    supplier?: string;
    assigned_to?: number;
    viewed?: string[];
    page?: number;
    limit?: number;
  }): Promise<{ pendingProducts: PendingProduct[]; pagination: PaginationInfo }> => {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        if (key === 'viewed' && Array.isArray(value)) {
          // Handle viewed array parameter
          value.forEach(v => queryParams.append('viewed', v));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });

    const response = await apiClient.get<{ pendingProducts: PendingProduct[]; pagination: PaginationInfo }>(`/products/pending?${queryParams}`);
    return response.data!;
  },

  assignItemCode: async (pendingProductId: number, itemCode: number): Promise<{ pendingProduct: PendingProduct; supplyOrder: any }> => {
    const response = await apiClient.patch<{ pendingProduct: PendingProduct; supplyOrder: any }>(`/products/pending/${pendingProductId}/assign`, { item_code: itemCode });
    return response.data!;
  },

  deletePendingProduct: async (pendingProductId: number): Promise<void> => {
    await apiClient.delete(`/products/pending/${pendingProductId}`);
  },

  // Follow-ups for pending products
  addFollowUp: async (pendingProductId: number, notes: string, photos: string[] = []): Promise<void> => {
    await apiClient.post(`/products/pending/${pendingProductId}/follow-up`, {
      notes,
      photos
    });
  },

  // Assign user to pending product
  assignPendingProductUser: async (pendingProductId: number, userId: number): Promise<PendingProduct> => {
    const response = await apiClient.patch<{ data: PendingProduct }>(`/products/pending/${pendingProductId}/assign-user`, { user_id: userId });
    return response.data!.data;
  },

  // Set assigned user for pending product
  setAssignedPendingProductUser: async (pendingProductId: number, userId: number): Promise<PendingProduct> => {
    const response = await apiClient.patch<{ data: PendingProduct }>(`/products/pending/${pendingProductId}/assigned-user`, { user_id: userId });
    return response.data!.data;
  },
};
